import { CreatedByAPIResponse, CreatedByType, ModuleItemAPIResponse, ModuleItemType, SubmoduleItem, SubmoduleItemAPIResponse } from 'app/types/modules';
import { getCourseItems, getLessons } from 'core/crud/lessons.crud';
import { getPreviewParam } from 'core/utils/utils';

/**
 * Transforms CreatedBy data from API format to internal format
 */
function transformCreatedBy(createdBy: CreatedByAPIResponse): CreatedByType {
  return {
    role_guid: createdBy.role_guid,
    avatar: createdBy.avatar || '',
    name: createdBy.name,
    lastname: createdBy.lastname,
    username: createdBy.username,
  };
}

/**
 * Transforms a submodule from API format to internal format
 */
function transformSubmoduleFromAPI(submodule: SubmoduleItemAPIResponse): SubmoduleItem {
  return {
    guid: submodule.guid,
    visibility: submodule.visibility,
    image: submodule.image || undefined,
    lesson_name: submodule.lesson_name,
    order: submodule.order,
    parent_guid: submodule.parent_guid,
    created_by: transformCreatedBy(submodule.created_by),
    updated_by: transformCreatedBy(submodule.updated_by),
    evaluation_period_id: submodule.evaluation_period_id,
    lesson_guid: submodule.lesson_guid,
    status: submodule.status,
    created_at: submodule.created_at,
    updated_at: submodule.updated_at,
  };
}

/**
 * Transforms a module from API format to internal format
 */
function transformModuleFromAPI(module: ModuleItemAPIResponse): ModuleItemType {
  // Sort submodules by order
  const sortedSubmodules = module.items ? module.items.map(transformSubmoduleFromAPI).sort((a, b) => a.order - b.order) : [];

  return {
    guid: module.guid,
    visibility: module.visibility,
    is_accessible: module.is_accessible,
    section: module.section || module.lesson_name,
    lesson_name: module.lesson_name,
    order: module.order,
    image: module.image || undefined,
    items: sortedSubmodules,
    created_by: transformCreatedBy(module.created_by),
    updated_by: transformCreatedBy(module.updated_by),
    evaluation_period_id: module.evaluation_period_id,
    lesson_guid: module.lesson_guid,
    status: module.status,
    created_at: module.created_at,
    updated_at: module.updated_at,
  };
}

/**
 * Service to get course items
 */
export async function getCourseItemsService(
  courseGuid: string,
  schoolGuid: string,
  customizedBy: string[],
  enableOverlays: boolean,
  isDisregardedProgram: boolean
): Promise<unknown> {
  if (!enableOverlays || isDisregardedProgram) {
    const response = await getLessons({ guid: courseGuid, includeKanban: true, includeDates: true, preview: isDisregardedProgram ? 1 : getPreviewParam() });
    return response?.data?.data ?? response;
  }

  // schoolGuid may be undefined — supported by API contract for students without a school. Do not guard.
  const response = await getCourseItems(courseGuid, schoolGuid, customizedBy);
  if (!response?.data?.data) {
    return [];
  }

  const apiData: ModuleItemAPIResponse[] = response.data.data?.items || [];
  const sortedModules = apiData.map(transformModuleFromAPI).sort((a, b) => a.order - b.order);
  return { items: sortedModules };
}
